import React, { useState } from 'react';
import { FaDownload, FaFileAlt, FaUpload, FaTrash, FaTimes, FaEye } from 'react-icons/fa';
import Button from '../../../../components/common/Button';
import MultiFileInput from '../../../../components/common/MultiFileInput';
import { API_BASE_URL } from '../../../../config';
import api from '../../../../services/api';

const AdditionalFilesModal = ({ show, onHide, files = [], requisitionId, onFilesUpdated }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  // Helper function to get file type from extension
  const getFileType = (fileName) => {
    if (!fileName) return 'Unknown';
    
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'PDF Document';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'xls':
      case 'xlsx':
        return 'Excel Spreadsheet';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint';
      case 'jpg':
      case 'jpeg':
        return 'JPEG Image';
      case 'png':
        return 'PNG Image';
      case 'gif':
        return 'GIF Image';
      case 'txt':
        return 'Text File';
      case 'zip':
      case 'rar':
      case '7z':
        return 'Archive';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'Video';
      case 'mp3':
      case 'wav':
        return 'Audio';
      default:
        return extension ? extension.toUpperCase() : 'Unknown';
    }
  };

  if (!show) return null;

  const handleDownload = async (file, forceDownload = false) => {
    try {
      // Check if this is a new standardized attachment (has attachment_id)
      if (file.attachment_id) {
        // Use centralized attachments download endpoint
        const downloadUrl = `${API_BASE_URL}/api/attachments/requisitions/${file.file_path}${forceDownload ? '?download=1' : ''}`;
        if (forceDownload) {
          // Force download
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = file.file_name || file.name || 'download';
          link.click();
        } else {
          // Open in new window for viewing
          window.open(downloadUrl, '_blank');
        }
      } else {
        // Legacy files - use backend for downloads, direct access for viewing
        if (forceDownload) {
          // Use backend controller for forced download
          const downloadUrl = `${API_BASE_URL}/files/storage?file=${encodeURIComponent(file.file_path || file.url)}`;
          window.open(downloadUrl, '_blank');
        } else {
          // Use direct file access for viewing
          const viewUrl = `${API_BASE_URL.replace('/portal', '')}/${file.file_path || file.url}`;
          window.open(viewUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file');
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setUploadMessage('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadMessage('');

    try {
      // Use the new standardized attachment upload endpoint
      const formData = new FormData();
      
      selectedFiles.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      // Use the centralized attachments endpoint
      const response = await api.post(`/api/attachments/requisitions/${requisitionId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.status === 1) {
        setUploadMessage('Files uploaded successfully!');
        setSelectedFiles([]);
        
        // Notify parent component to refresh data
        if (onFilesUpdated) {
          onFilesUpdated();
        }
      } else {
        // Fallback to legacy upload if new endpoint not available yet
        console.log('New endpoint not available, falling back to legacy upload');
        const legacyFormData = new FormData();
        legacyFormData.append('requisition_id', requisitionId);
        
        selectedFiles.forEach((file, index) => {
          legacyFormData.append(`files[]`, file);
        });

        const legacyResponse = await api.post('/api/requisitions/update_files', legacyFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (legacyResponse.data && legacyResponse.data.status === 1) {
          setUploadMessage('Files uploaded successfully!');
          setSelectedFiles([]);
          
          if (onFilesUpdated) {
            onFilesUpdated();
          }
        } else {
          throw new Error(legacyResponse.data?.message || 'Upload failed');
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadMessage('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async (fileId, filePath) => {
    if (!window.confirm('Are you sure you want to remove this file?')) {
      return;
    }

    try {
      // Use the api service for proper authentication
      const response = await api.get(`/api/requisitions/remove_file/${fileId}?file=${encodeURIComponent(filePath)}`);

      if (response.data && response.data.status === 1) {
        setUploadMessage('File removed successfully!');
        // Notify parent component to refresh data
        if (onFilesUpdated) {
          onFilesUpdated();
        }
      } else {
        throw new Error(response.data?.message || 'Remove failed');
      }
    } catch (error) {
      console.error('Error removing file:', error);
      setUploadMessage('Error removing file. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Additional Files</h2>
          <button
            onClick={onHide}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Modal Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(95vh - 140px)' }}>
          {uploadMessage && (
            <div className={`mb-4 p-4 rounded-lg border ${
              uploadMessage.includes('Error') 
                ? 'bg-red-50 border-red-200 text-red-800' 
                : 'bg-green-50 border-green-200 text-green-800'
            }`}>
              {uploadMessage}
            </div>
          )}

          {/* File Upload Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Upload Supporting Documents</h3>
            <div className="space-y-3">
              <MultiFileInput
                files={selectedFiles}
                onChange={setSelectedFiles}
                multiple={true}
                className="w-full"
              />

              <Button
                variant="success"
                onClick={handleUpload}
                disabled={uploading || selectedFiles.length === 0}
                icon={FaUpload}
                className="w-full sm:w-auto"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </Button>
            </div>
          </div>

          <hr className="border-gray-200 my-4" />

          {/* Existing Files Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Existing Supporting Documents</h3>
            {files && files.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto" style={{ maxHeight: '400px' }}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          File Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {files.map((file, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center min-w-0">
                              <FaFileAlt className="mr-2 text-gray-400 flex-shrink-0" />
                              <span className="text-sm text-gray-900 truncate" title={file.file_name || file.name || `File ${index + 1}`}>
                                {file.file_name || file.name || `File ${index + 1}`}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full" 
                                  style={{
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151'
                                  }}>
                              {getFileType(file.file_name || file.name)}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleDownload(file, false)}
                                icon={FaEye}
                                className="text-xs px-2 py-1 min-w-0 flex-shrink-0"
                              > 
                                <span className="sm:hidden">V</span>
                              </Button>
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleDownload(file, true)}
                                icon={FaDownload}
                                className="text-xs px-2 py-1 min-w-0 flex-shrink-0"
                              > 
                                <span className="sm:hidden">DL</span>
                              </Button>
                              {file.file_id && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleRemoveFile(file.file_id, file.file_path)}
                                  icon={FaTrash}
                                  className="text-xs px-2 py-1 min-w-0 flex-shrink-0"
                                > 
                                  <span className="sm:hidden">RM</span>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FaFileAlt size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No additional files available</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 flex-shrink-0">
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdditionalFilesModal;
